import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import {
  canLoad,
  deleteSave,
  exportSave,
  importSave,
  listSaves,
  loadSave,
  type SaveMeta,
} from '../saves';
import { type StartMode, useGame } from '../store';
import { Icon } from './icons';
import { SaveSummary } from './SaveSummary';

/** Loads a save's data and starts it, alone or as a co-op host. */
export async function startSave(
  meta: SaveMeta,
  how: 'offline' | 'host',
  onStart: (mode: StartMode) => void,
): Promise<boolean> {
  const record = await loadSave(meta.id);
  if (!record) return false;
  const save = { id: record.id, name: record.name, data: record.data, history: record.history };
  const relayUrl = useGame.getState().relayUrl;
  onStart(how === 'offline' ? { kind: 'offline', save } : { kind: 'host', relayUrl, save });
  return true;
}

/** The player's saved games (on the server): load (alone or co-op), back up, import, delete. */
export function SavesPage({
  onStart,
  onChange,
}: {
  onStart: (mode: StartMode) => void;
  onChange: () => void;
}) {
  const t = useT();
  const [saves, setSaves] = useState<SaveMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = () => {
    listSaves()
      .then(setSaves)
      .catch(() => {
        setSaves([]);
        setError(t('save.storageError'));
      });
    onChange();
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once when the page opens
  useEffect(refresh, []);

  const load = (meta: SaveMeta, how: 'offline' | 'host') => {
    startSave(meta, how, onStart)
      .then((ok) => ok || setError(t('save.loadError')))
      .catch(() => setError(t('save.loadError')));
  };

  return (
    <div className="sheet-body">
      <p className="sheet-lead">{t('save.lead')}</p>
      {error && (
        <div className="banner error" role="alert">
          <Icon name="warning" size={18} />
          {error}
        </div>
      )}
      {saves && saves.length === 0 && <p className="muted">{t('save.none')}</p>}
      {saves && saves.length > 0 && (
        <ul className="save-list">
          {saves.map((s) => (
            <li key={s.id} className="save-card">
              <SaveSummary save={s} />
              <div className="save-actions">
                <button
                  type="button"
                  className="btn primary"
                  disabled={!canLoad(s)}
                  onClick={() => load(s, 'offline')}
                >
                  <Icon name="play" size={15} />
                  {t('save.load')}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!canLoad(s)}
                  title={t('save.hostHint')}
                  onClick={() => load(s, 'host')}
                >
                  <Icon name="users" size={15} />
                  {t('save.host')}
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  title={t('save.export')}
                  onClick={() => void exportSave(s.id)}
                >
                  <Icon name="download" size={16} />
                </button>
                <button
                  type="button"
                  className={`icon-btn ${confirmDelete === s.id ? 'danger' : ''}`}
                  title={confirmDelete === s.id ? t('save.deleteConfirm') : t('save.delete')}
                  onClick={() => {
                    if (confirmDelete !== s.id) {
                      setConfirmDelete(s.id);
                      return;
                    }
                    setConfirmDelete(null);
                    deleteSave(s.id).then(refresh, refresh);
                  }}
                >
                  <Icon name="trash" size={16} />
                  {confirmDelete === s.id && (
                    <span className="small">{t('save.deleteConfirm')}</span>
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="save-import">
        <input
          ref={fileRef}
          type="file"
          accept=".am27"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            setError(null);
            importSave(file).then(refresh, () => setError(t('save.importError')));
          }}
        />
        <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
          <Icon name="upload" size={16} />
          {t('save.import')}
        </button>
        <span className="muted small">{t('save.backupHint')}</span>
      </div>
    </div>
  );
}
